<script setup>
/**
 * "Framework in use" (PD-1292) — the register plotted on the framework grid.
 *
 * Career tracks across, levels down, each cell holding the roles that landed at
 * that intersection, with anchor coverage scored per cell against the floor.
 * Ported from the design prototype's LvlFrameworkGrid (lvl/framework-grid.jsx).
 *
 * THE MODEL, NOT THE REGISTER. The shape is read from the real ladder, so it is
 * deliberately STEPPED: most level/track intersections do not exist, and those are
 * drawn as structurally unavailable rather than as empty gaps. An empty cell that
 * DOES exist is a level nobody sits at — also not a gap.
 *
 * One difference from the prototype, which had a single implicit ladder: the real
 * framework is three-dimensional (level x function x track), so a Function selector
 * picks which ladder is on screen. Family stays what it is in the prototype — a
 * filter over roles, not an axis — and comes from the job hierarchy.
 *
 * Placement comes from the EVALUATION (see getFrameworkInUse), never from the
 * document's levelling template attributes, so a cell's roles and its anchors can
 * never be counted from two different sources.
 */
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { ANCHOR_FLOOR, anchorNeed } from '@rm-ui/levelling/anchorSetup';
import CcAnchorModal from '@/components/levelling/controlCentre/CcAnchorModal.vue';

const props = defineProps({
  /** The framework as framework.vue holds it: levels, functions, tracks, level_function_tracks + labels. */
  levellingFramework: { type: Object, required: true },
  /** The markRaw'd levellingAnchors() self object, owned by framework.vue so it loads once. */
  anchors: { type: Object, required: true },
  /* Whether this tab is the one on screen. The view is mounted once and then shown
     and hidden, so it has to be told when it comes back into view — otherwise it
     would go on showing whatever the register looked like on first open. */
  active: { type: Boolean, default: true },
});

const emit = defineEmits(['evaluate']);

const loading = ref(true);
const error = ref('');
const roles = ref([]);
const ceiling = ref(null);
const canGovern = ref(false);
const enabled = ref(false);
/* Server's read of the register size, which picks the default floor. The user can
   override it in the bar — larger or more dispersed organisations need more anchors
   per cell to calibrate reliably, and only they know which they are. */
const orgSize = ref('standard');

/* Whole register first: the grid's job is to show how the organisation actually uses
   the framework, and a single family is a drill-down from that, not the default view. */
const scope = ref('org');
const groupSel = ref(null);
const familySel = ref(null);
const functionSel = ref(null);
const cov = ref(false);
const cell = ref(null);      // the selected lft row
const ask = ref(null);       // role awaiting the confirm modal

/* ---------------------------------------------------------------- framework shape */

const lf = computed(() => props.levellingFramework || {});
const levelLabel = computed(() => lf.value.level_label || 'Level');
const trackLabel = computed(() => lf.value.track_label || 'Track');
const functionLabel = computed(() => lf.value.function_label || 'Function');

const tracks = computed(() => [...(lf.value.tracks || [])].sort((a, b) => a.position - b.position));
const functions = computed(() => [...(lf.value.functions || [])].sort((a, b) => a.position - b.position));
/* Most senior first, like the prototype's ladder sort. `position` is the rank; the
   level NAME is a client string and is not comparable. */
const levels = computed(() => [...(lf.value.levels || [])].sort((a, b) => b.position - a.position));

const activeFunctionId = computed(() => {
  const list = functions.value;
  if (functionSel.value && list.some(f => f.lf_function_id === functionSel.value)) return functionSel.value;
  const core = list.find(f => f.is_core);
  return (core || list[0] || {}).lf_function_id ?? null;
});

/** Cell lookup for the function on screen: "<level>|<track>" -> the lft row. */
const cellIndex = computed(() => {
  const map = {};
  const fnId = activeFunctionId.value;
  for (const lft of lf.value.level_function_tracks || []) {
    if (lft.lf_function_id !== fnId) continue;
    map[`${lft.lf_level_id}|${lft.lf_track_id}`] = lft;
  }
  return map;
});
const cellAt = (level, track) => cellIndex.value[`${level.lf_level_id}|${track.lf_track_id}`] || null;

/* ------------------------------------------------------------------ role scoping */

const junk = /^[-–—\s]+$/; // dashes and blanks are not family names

/* Roles on the ladder currently displayed. A role evaluated against another
   function sits on a different ladder, so it is not on this grid at all — the
   explain line says how many, rather than silently dropping them. */
const functionRoles = computed(() =>
  roles.value.filter(r => r.lf_function_id === activeFunctionId.value));

const groups = computed(() =>
  [...new Set(functionRoles.value.map(r => r.familyGroup).filter(g => g && !junk.test(g)))].sort());

const activeGroup = computed(() => {
  if (groupSel.value && groups.value.includes(groupSel.value)) return groupSel.value;
  // Default to the biggest group — most likely the part of the business being levelled.
  const counts = {};
  for (const r of functionRoles.value) {
    if (r.familyGroup && !junk.test(r.familyGroup)) counts[r.familyGroup] = (counts[r.familyGroup] || 0) + 1;
  }
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null;
});

const families = computed(() => [...new Set(functionRoles.value
  .filter(r => !activeGroup.value || r.familyGroup === activeGroup.value)
  .map(r => r.family).filter(Boolean))].sort());

const activeFamily = computed(() =>
  (familySel.value && families.value.includes(familySel.value) ? familySel.value : families.value[0]) || null);

const scopeRoles = computed(() => (scope.value === 'org'
  ? functionRoles.value
  : functionRoles.value.filter(r => r.family === activeFamily.value)));

/** lf_level_function_track_id -> the roles sitting in that cell. */
const byCell = computed(() => {
  const map = {};
  for (const r of scopeRoles.value) {
    if (!r.lf_level_function_track_id) continue;
    (map[r.lf_level_function_track_id] = map[r.lf_level_function_track_id] || []).push(r);
  }
  return map;
});
const rolesIn = lft => (lft && byCell.value[lft.lf_level_function_track_id]) || [];

/* Levelled, but on a level/function/track trio this framework does not define —
   worth naming, because such a role is invisible on every cell of the grid. */
const offLadder = computed(() =>
  scopeRoles.value.filter(r => r.lf_level_id && !r.lf_level_function_track_id).length);
const unlevelled = computed(() => scopeRoles.value.filter(r => !r.lf_level_id).length);

/* -------------------------------------------------------------------- anchor state */

const isAnchor = r => !!r.isAnchor;
/* A PENDING anchor is a claim, not evidence: no evaluation stands behind it, so it
   marks its cell but cannot count towards a floor that exists to guarantee one. */
const isConfirmed = r => !!r.isAnchor && !r.isPending;
const anchorIn = lft => rolesIn(lft).find(isAnchor) || null;
const confirmedIn = lft => rolesIn(lft).filter(isConfirmed).length;

const floor = computed(() => ANCHOR_FLOOR[orgSize.value] ?? ANCHOR_FLOOR.standard);
const needIn = lft => anchorNeed(floor.value, rolesIn(lft).length);

const ceilingBand = computed(() =>
  (ceiling.value && ceiling.value.levelPosition != null ? Number(ceiling.value.levelPosition) : null));

/* One suggestion per track: the topmost occupied cell in a track with no anchor
   anywhere. No occupied cell means no suggestion — we do not guess seniority.
   Family-scoped only; the coverage overlay keeps the grid quiet. */
const suggested = computed(() => {
  const out = {};
  if (cov.value || scope.value === 'org') return out;
  for (const t of tracks.value) {
    const cells = levels.value.map(l => cellAt(l, t)).filter(Boolean);
    if (cells.some(c => anchorIn(c))) continue;
    const top = cells.find(c => rolesIn(c).length);
    if (top) out[t.lf_track_id] = top.lf_level_function_track_id;
  }
  return out;
});
const isSuggested = lft => !!lft && suggested.value[lft.lf_track_id] === lft.lf_level_function_track_id;

/* Denominator is POPULATED cells only: a level and track nobody sits at is not a
   gap, and counting it as one would make the floor unreachable by design. */
const covStats = computed(() => {
  let met = 0, pop = 0;
  for (const l of levels.value) {
    for (const t of tracks.value) {
      const lft = cellAt(l, t);
      if (!lft) continue;
      const n = rolesIn(lft).length;
      if (!n) continue;
      pop++;
      if (confirmedIn(lft) >= anchorNeed(floor.value, n)) met++;
    }
  }
  return { met, pop };
});

const populatedTracks = computed(() => tracks.value.filter(t =>
  levels.value.some(l => rolesIn(cellAt(l, t)).length)).length);
const anchoredTracks = computed(() => tracks.value.filter(t =>
  levels.value.some(l => anchorIn(cellAt(l, t)))).length);

const maxCount = computed(() => Math.max(1, ...Object.values(byCell.value).map(a => a.length)));

/* ------------------------------------------------------------------------ display */

const cellRoles = computed(() => rolesIn(cell.value));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const cellTip = (lft, level, track) => {
  const list = rolesIn(lft);
  const anch = anchorIn(lft);
  const code = lft.abbreviation || `${track.abbreviation || ''}${level.name}`;
  const head = `${code} · ${lft.name || level.name}`;
  let tip = anch
    ? `${head} - anchor: ${anch.title}${anch.isPending ? ' (pending - not yet counted towards coverage)' : ''}`
    : `${head}${list.length ? ` - ${plural(list.length, 'role')}` : ' - no roles sit here yet'}`
      + (isSuggested(lft) ? ` · suggested anchor for this ${trackLabel.value.toLowerCase()}` : '');
  if (isAbove(level)) tip += ' · above the framework ceiling';
  if (cov.value && list.length) {
    const need = anchorNeed(floor.value, list.length);
    tip += ` · ${anch ? confirmedIn(lft) : 0} of ${need} anchors towards the floor`;
    if (list.length < floor.value) {
      tip += ` (only ${plural(list.length, 'role')} sits here, so the floor of ${floor.value} cannot apply)`;
    }
  }
  return tip;
};

const codeOf = (lft, level, track) => lft.abbreviation || `${track.abbreviation || ''}${level.name}`;
const isAbove = level => ceilingBand.value != null && Number(level.position) > ceilingBand.value;
const isCeilingRow = level => ceilingBand.value != null && Number(level.position) === ceilingBand.value;

/* Density tint, so an occupied cell reads at a glance without a number on it. */
const cellStyle = (lft) => {
  const n = rolesIn(lft).length;
  if (!n) return null;
  const d = Math.min(1, 0.25 + (n / maxCount.value) * 0.75);
  return { background: `color-mix(in srgb, var(--cc-accent, #4f46e5) ${Math.round(d * 18)}%, var(--cc-surface, #fff))` };
};

const roleState = (r) => (r.evaluated ? 'Evaluated' : r.lf_level_id ? 'Classified' : 'Not levelled');

/* ------------------------------------------------------------------------- actions */

const selectCell = (lft, level, track) => {
  cell.value = cell.value && cell.value.lf_level_function_track_id === lft.lf_level_function_track_id
    ? null
    : { ...lft, _level: level, _track: track };
};

/** CcAnchorModal expects a control-centre row; these roles carry the same facts. */
const modalRole = computed(() => (ask.value ? {
  doc_id: ask.value.doc_id,
  doc_name: ask.value.title,
  doc_title: ask.value.title,
  system_number: ask.value.reference,
  levelling: {
    method: ask.value.method,
    evaluation_status: ask.value.evaluationStatus,
    level_code: ask.value.levelCode,
    is_anchor: ask.value.isAnchor,
    is_ceiling: ask.value.isCeiling,
    is_pending: ask.value.isPending,
  },
} : null));

/* An anchor can only ever be set from a completed FULL evaluation, so "evaluate to
   anchor" is a route into the journey, not a shortcut that writes anything. */
const goEvaluate = (role) => emit('evaluate', role);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const resp = await axios.post('document/frameworkInUse', {});
    const data = (resp && resp.data && resp.data.Data) || {};
    roles.value = Array.isArray(data.roles) ? data.roles : [];
    ceiling.value = data.ceiling || null;
    canGovern.value = !!data.canGovern;
    enabled.value = !!data.enabled;
    orgSize.value = data.orgSize || 'standard';
  } catch (e) {
    error.value = 'Could not load the register for this framework.';
    roles.value = [];
  } finally {
    loading.value = false;
  }
}

async function onAnchorSaved() {
  ask.value = null;
  // The anchor flags live on the roles payload, so the grid is only correct again
  // once it is re-read; the shared composable keeps the modal and chips in step.
  await Promise.all([load(), props.anchors.load()]);
}

onMounted(load);
watch(() => props.active, (on, was) => { if (on && was === false) load(); });
</script>

<template>
  <div class="lvl-fw">
    <div v-if="loading" class="lvl-fw-loading">Loading the register&hellip;</div>
    <div v-else-if="error" class="lvl-fw-msg is-error">{{ error }}</div>
    <div v-else-if="!enabled" class="lvl-fw-msg">
      Anchor roles are not switched on for this client, so there is no coverage to show.
    </div>

    <template v-else>
      <!-- ============================= intro ============================= -->
      <div class="lvl-fwal-intro" data-tour="fwiu-intro">
        <span class="lvl-fwal-intro-ic"><v-icon size="17">mdi-view-grid-outline</v-icon></span>
        <div class="lvl-fwal-intro-main">
          <span class="t">Where your roles actually sit on the framework</span>
          <span class="s">
            <!-- The design hardcodes "Career tracks"; the label is client-configurable and
                 is often already "Career Stream", so the prefix would double up. It also
                 opens the sentence, so it keeps its own capitalisation. -->
            {{ trackLabel }}s across, the {{ levels.length }}
            {{ levelLabel.toLowerCase() }}s down, your roles counted in each cell &mdash; so you can see
            which parts of the framework your organisation really uses and which are empty. Select any
            cell to list its roles or set an anchor. Use <strong>Coverage</strong> to score each
            populated cell against the anchor floor.<template v-if="functions.length > 1">
            One {{ functionLabel.toLowerCase() }} is shown at a time, because each has its own
            rungs.</template>
          </span>
        </div>
      </div>

      <!-- ============================ controls ============================ -->
      <div class="lvl-fw-bar">
        <div class="lvl-fwf-grp">
          <span class="lvl-fwf-lbl">{{ functionLabel }}</span>
          <div class="lvl-fwseg" role="tablist" :aria-label="functionLabel">
            <button
              v-for="f in functions"
              :key="f.lf_function_id"
              type="button"
              role="tab"
              class="lvl-fwseg-btn"
              :class="{ on: f.lf_function_id === activeFunctionId }"
              :aria-selected="f.lf_function_id === activeFunctionId"
              @click="functionSel = f.lf_function_id; cell = null"
            >{{ f.name }}</button>
          </div>
        </div>

        <div class="lvl-fwf-grp">
          <span class="lvl-fwf-lbl">Scope</span>
          <div class="lvl-fwseg" role="tablist" aria-label="Grid scope">
            <button
              type="button" role="tab" class="lvl-fwseg-btn" :class="{ on: scope === 'org' }"
              :aria-selected="scope === 'org'"
              @click="scope = 'org'; cell = null"
            >Whole register</button>
            <button
              type="button" role="tab" class="lvl-fwseg-btn" :class="{ on: scope === 'family' }"
              :aria-selected="scope === 'family'"
              @click="scope = 'family'; cell = null"
            >Family</button>
          </div>
        </div>

        <template v-if="scope === 'family'">
          <select
            v-if="groups.length"
            class="lvl-fw-sel"
            :value="activeGroup || ''"
            aria-label="Family group"
            @change="groupSel = $event.target.value; familySel = null; cell = null"
          >
            <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
          </select>
          <select
            v-if="families.length"
            class="lvl-fw-sel"
            :value="activeFamily || ''"
            aria-label="Job family"
            @change="familySel = $event.target.value; cell = null"
          >
            <option v-for="f in families" :key="f" :value="f">{{ f }}</option>
          </select>
        </template>

        <div class="lvl-fwf-grp" data-tour="fwiu-show">
          <span class="lvl-fwf-lbl">Show</span>
          <div class="lvl-fwseg" role="tablist" aria-label="Grid overlay">
            <button
              type="button" role="tab" class="lvl-fwseg-btn" :class="{ on: !cov }"
              :aria-selected="!cov" @click="cov = false"
            >Roles</button>
            <button
              type="button" role="tab" class="lvl-fwseg-btn" :class="{ on: cov }"
              :aria-selected="cov" @click="cov = true"
            >Coverage</button>
          </div>
        </div>

        <select
          v-if="cov"
          v-model="orgSize"
          class="lvl-fw-sel lvl-fw-sel--floor"
          aria-label="Anchor target per cell"
          title="How many evaluated anchor roles a populated cell needs before it counts as covered. Larger or more dispersed organisations need more anchors per cell to calibrate reliably."
        >
          <option value="standard">Target: {{ ANCHOR_FLOOR.standard }} anchors per cell</option>
          <option value="large">Target: {{ ANCHOR_FLOOR.large }} &ndash; large / dispersed org</option>
        </select>

        <span class="lvl-fw-key" role="img" aria-label="Legend">
          <span class="lvl-fw-key-lbl">Key</span>
          <template v-if="cov">
            <span class="lvl-fw-key-i"><span class="lvl-fw-cov is-met">{{ floor }}/{{ floor }}</span> Met</span>
            <span class="lvl-fw-key-i"><span class="lvl-fw-cov is-thin">1/{{ floor }}</span> Thin</span>
            <span class="lvl-fw-key-i"><span class="lvl-fw-cov is-gap">0/{{ floor }}</span> Gap</span>
          </template>
          <span class="lvl-fw-key-i"><span class="sw is-anch"><v-icon size="10" color="#fff">mdi-anchor</v-icon></span> Anchor</span>
          <span v-if="!cov" class="lvl-fw-key-i"><span class="sw is-sugg"></span> Suggested</span>
          <span class="lvl-fw-key-i"><span class="sw is-na"></span> Not in {{ trackLabel.toLowerCase() }}</span>
        </span>
      </div>

      <!-- ============================ explain ============================ -->
      <div class="lvl-fw-explain">
        <span class="lvl-fw-explain-ic">
          <v-icon size="17">{{ cov ? 'mdi-ruler' : 'mdi-chart-box-outline' }}</v-icon>
        </span>
        <div class="lvl-fw-explain-main">
          <span class="t">
            <template v-if="cov">{{ covStats.pop
              ? `Coverage: ${covStats.met} of ${plural(covStats.pop, 'populated cell')} meet the floor`
              : 'Nothing to score yet' }}</template>
            <template v-else>{{ plural(scopeRoles.length, 'role') }}<template
              v-if="populatedTracks"> &middot; {{ anchoredTracks }} of {{ plural(populatedTracks, 'populated ' + trackLabel.toLowerCase()) }} anchored</template></template>
            <template v-if="unlevelled"> &middot; {{ unlevelled }} not yet levelled</template>
            <template v-if="offLadder"> &middot; {{ offLadder }} off this ladder</template>
          </span>
          <span v-if="!populatedTracks" class="s">
            No roles here carry a {{ levelLabel.toLowerCase() }} yet, so there are no populated cells to
            score. Level some roles first &mdash; then anchors can be set and coverage will show which
            cells have enough evaluated anchors to calibrate against.
          </span>
          <span v-else class="s">
            Each cell counts the roles levelled at that {{ trackLabel.toLowerCase() }} and
            {{ levelLabel.toLowerCase() }}; anchored cells carry an evaluated reference. Empty cells are
            {{ levelLabel.toLowerCase() }}s nobody sits at, not gaps.
          </span>
        </div>
      </div>

      <!-- ============================== grid ============================== -->
      <div class="lvl-fw-body">
        <div class="lvl-fw-gridwrap">
          <div
            class="lvl-fw-grid"
            data-tour="fwiu-grid"
            :style="{ gridTemplateColumns: `116px repeat(${tracks.length}, minmax(96px, 1fr))` }"
          >
            <div class="lvl-fw-corner">{{ levelLabel }}</div>
            <div v-for="t in tracks" :key="'h' + t.lf_track_id" class="lvl-fw-colh">
              <span class="tk" :style="{ background: t.background_colour || 'var(--cc-text-3, #97a0af)' }">
                {{ t.abbreviation || t.name.slice(0, 1) }}
              </span>{{ t.name }}
            </div>

            <template v-for="l in levels">
              <div
                :key="'r' + l.lf_level_id"
                class="lvl-fw-rowh"
                :class="{ 'is-above': isAbove(l), 'is-ceil': isCeilingRow(l) }"
              >
                <span class="g">{{ l.name }}</span>
                <span
                  v-if="isCeilingRow(l)" class="cl"
                  :title="`Framework ceiling: ${ceiling.title}${ceiling.levelCode ? ' (' + ceiling.levelCode + ')' : ''}`"
                ><v-icon size="11">mdi-arrow-collapse-up</v-icon> Ceiling</span>
                <span v-else-if="isAbove(l)" class="cl">Above ceiling</span>
              </div>

              <!-- One wrapper per slot, and it is the wrapper that is keyed. Vuetify's
                   detachable mixin lifts the activator OUT of <span class="v-tooltip"> and
                   re-inserts it beside the span, behind Vue's back — Vue still thinks the
                   slot is the span alone. With the span as a direct grid child, switching
                   function (which turns na slots into cells and back) made Vue insert and
                   move spans while the orphaned buttons stayed put, piling up at the end
                   and shifting every row into the wrong columns. Inside a wrapper the
                   lifted button stays in the wrapper, and Vue moves the whole thing. -->
              <div
                v-for="t in tracks"
                :key="`s-${l.lf_level_id}-${t.lf_track_id}`"
                class="lvl-fw-slot"
              >
                <div
                  v-if="!cellAt(l, t)"
                  key="na"
                  class="lvl-fw-cell is-na"
                  aria-hidden="true"
                ></div>
                <!-- Hover intent. Cells are small and packed, and with no delay a
                     sweep across a row opens every tooltip it touches — several are
                     on screen at once, because each opens instantly while the last
                     is still fading out. Vuetify clears a pending open on mouseleave,
                     so a delay means a sweep opens nothing and only settling on a
                     cell shows its tip. -->
                <v-tooltip
                  v-else
                  key="c"
                  bottom
                  content-class="fw-tip"
                  open-delay="350"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="lvl-fw-cell"
                      :class="{
                        'is-anch': !!anchorIn(cellAt(l, t)),
                        'is-sugg': isSuggested(cellAt(l, t)),
                        'is-above': isAbove(l),
                        'is-sel': cell && cell.lf_level_function_track_id === cellAt(l, t).lf_level_function_track_id,
                        'is-empty': !rolesIn(cellAt(l, t)).length,
                      }"
                      :style="cellStyle(cellAt(l, t))"
                      v-bind="attrs"
                      v-on="on"
                      @click="selectCell(cellAt(l, t), l, t)"
                    >
                      <span class="lvl-fw-code">{{ codeOf(cellAt(l, t), l, t) }}</span>
                      <span
                        v-if="anchorIn(cellAt(l, t))"
                        class="lvl-fw-mark"
                        :class="{ 'is-pending': anchorIn(cellAt(l, t)).isPending }"
                      >
                        <v-icon size="13" color="#fff">mdi-anchor</v-icon>
                        <b v-if="anchorIn(cellAt(l, t)).isCeiling">CEO</b>
                        <span v-if="!cov" class="lvl-fw-n">{{ rolesIn(cellAt(l, t)).length }}</span>
                      </span>
                      <span v-else-if="!cov && rolesIn(cellAt(l, t)).length" class="lvl-fw-n">
                        {{ rolesIn(cellAt(l, t)).length }}
                      </span>
                      <span v-else-if="isSuggested(cellAt(l, t))" class="lvl-fw-sugg">Suggested</span>
                      <span
                        v-if="cov && rolesIn(cellAt(l, t)).length"
                        class="lvl-fw-cov"
                        :class="confirmedIn(cellAt(l, t)) >= needIn(cellAt(l, t)) ? 'is-met'
                          : confirmedIn(cellAt(l, t)) ? 'is-thin' : 'is-gap'"
                      >{{ confirmedIn(cellAt(l, t)) }}/{{ needIn(cellAt(l, t)) }}</span>
                    </button>
                  </template>
                  <span>{{ cellTip(cellAt(l, t), l, t) }}</span>
                </v-tooltip>
              </div>
            </template>
          </div>
        </div>

        <!-- ============================= panel ============================= -->
        <div class="lvl-fw-panel" data-tour="fwiu-panel">
          <div v-if="!cell" class="lvl-fw-panel-empty">
            <v-icon size="22">mdi-view-grid-outline</v-icon>
            <p v-if="!populatedTracks">
              <strong>Nothing to select yet.</strong> Cells fill as roles are levelled.
            </p>
            <template v-else>
              <p><strong>Select a cell</strong> to see the roles at that {{ levelLabel.toLowerCase() }}
                and {{ trackLabel.toLowerCase() }}, and to set one as an anchor.</p>
              <p v-if="Object.keys(suggested).length" class="s">
                {{ plural(Object.keys(suggested).length, trackLabel.toLowerCase()) }} here
                {{ Object.keys(suggested).length === 1 ? 'has' : 'have' }} no anchor &mdash; the suggested
                cells are the most senior {{ levelLabel.toLowerCase() }} with roles in them.
              </p>
            </template>
          </div>

          <template v-else>
            <div class="lvl-fw-panel-h">
              <div>
                <span class="c">{{ codeOf(cell, cell._level, cell._track) }} &middot; {{ cell.name || cell._level.name }}</span>
                <span class="s">{{ cell._track.name }} &middot; {{ scope === 'org' ? 'Whole register' : activeFamily }}</span>
              </div>
              <button type="button" class="lvl-fw-close" aria-label="Close" @click="cell = null">
                <v-icon size="17">mdi-close</v-icon>
              </button>
            </div>

            <div v-if="!cellRoles.length" class="lvl-fw-panel-empty">
              <v-icon size="22">mdi-stairs</v-icon>
              <p>
                <strong>No role sits at {{ codeOf(cell, cell._level, cell._track) }} yet.</strong>
                A {{ levelLabel.toLowerCase() }} fills when a role is evaluated and lands on it. Until one
                does, there is nothing here to calibrate other roles against, and no anchor can be set.
              </p>
              <p v-if="isSuggested(cell)" class="s">
                This is the suggested anchor cell for the {{ trackLabel.toLowerCase() }} &mdash; the most
                senior {{ levelLabel.toLowerCase() }} with roles in it sits below.
              </p>
            </div>

            <div v-else class="lvl-fw-list">
              <div
                v-for="r in cellRoles"
                :key="r.doc_id"
                class="lvl-fw-role"
                :class="{ 'is-anch': r.isAnchor }"
              >
                <div class="lvl-fw-role-main">
                  <span class="t">{{ r.title }}</span>
                  <span class="s">
                    {{ r.reference }} &middot; {{ roleState(r) }}<template
                      v-if="r.isAnchor"> &middot; anchor<template
                        v-if="r.isPending"> (pending)</template><template
                        v-if="r.isCeiling"> &middot; CEO ceiling</template></template>
                  </span>
                </div>
                <button
                  v-if="r.isAnchor && canGovern"
                  type="button" class="lvl-fw-route" @click="ask = r"
                >Manage</button>
                <button
                  v-else-if="r.evaluated && canGovern"
                  type="button" class="lvl-fw-set" @click="ask = r"
                ><v-icon size="14">mdi-anchor</v-icon> Set as anchor</button>
                <button
                  v-else-if="!r.evaluated"
                  type="button" class="lvl-fw-route" @click="goEvaluate(r)"
                >Evaluate to anchor</button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <CcAnchorModal
      v-if="ask && modalRole"
      :role="modalRole"
      kind="anchor"
      :anchors="anchors"
      @close="ask = null"
      @saved="onAnchorSaved"
      @evaluate="goEvaluate(ask)"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Ported from the prototype's lvl.css .lvl-fw-* block. The prototype's --v3-*
   tokens map to the app-wide --cc-* ramp (assets/styles/theme.scss); every one
   carries a literal fallback so the grid still reads if a token is missing. */

.lvl-fw {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* The tab frame (.framework-content > .v-card__text) is a fixed-height flex column
     with overflow:hidden, and every other tab puts its own scroller inside. This view
     is one content column rather than a table, so the view itself is that scroller —
     without this it is simply clipped at the fold with no way to reach the bottom.
     flex/min-height rather than the max-height: calc(100vh - Npx) used elsewhere,
     which is wrong inside the page shell, where the frame is height:100% of a wrap
     that is already offset by the page head. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* so the last card is not glued to the bottom edge of the scroll area */
  padding-bottom: 16px;

  /* A column flex container hands its children a default flex-shrink of 1, so with a
     definite height they COMPRESS to fit instead of overflowing — overflow-y above
     would then never have anything to scroll, and tall content would simply be
     squashed. Pinning the sections to their natural height is what turns the overflow
     into an actual scroll. */
  > * {
    flex-shrink: 0;
  }

  font-family: var(--cc-font, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
}

.lvl-fw-loading,
.lvl-fw-msg {
  padding: 24px 4px;
  font-size: 13px;
  color: var(--cc-text-2, #5b6472);
}
.lvl-fw-msg.is-error { color: var(--cc-danger, #d64545); }

/* ---------------------------------------------------------------------- intro */

.lvl-fwal-intro {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 12px;
  background: var(--cc-surface, #ffffff);
}
.lvl-fwal-intro-ic {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #4f46e5) 10%, transparent);
  color: var(--cc-accent, #4f46e5);

  /* Vuetify hard-sets .theme--light.v-icon to rgba(0,0,0,.54), so the icon ignores
     the colour on its container unless told to inherit it. */
  ::v-deep .v-icon { color: inherit; }
}
.lvl-fwal-intro-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .t { font-size: 14px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .s { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.55; text-wrap: pretty; }
}

/* ------------------------------------------------------------------- controls */

.lvl-fw-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.lvl-fwf-grp { display: inline-flex; align-items: center; gap: 8px; }
.lvl-fwf-lbl { font-size: 12px; font-weight: 600; color: var(--cc-text-2, #5b6472); }

.lvl-fwseg {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: var(--cc-surface-2, #f5f6f8);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  height: 40px;
}

.lvl-fwseg-btn {
  border: 0;
  cursor: pointer;
  border-radius: 999px;
  padding: 0 14px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  background: transparent;
  color: var(--cc-text-2, #5b6472);

  &.on {
    background: var(--cc-surface, #ffffff);
    color: var(--cc-text, #1f242d);
    box-shadow: var(--cc-shadow, 0 1px 2px rgba(16, 24, 40, .08));
  }
  &:focus-visible { outline: 2px solid var(--cc-accent, #4f46e5); outline-offset: 1px; }
}

.lvl-fw-sel {
  min-width: 170px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text, #1f242d);
  font-family: inherit;
  font-size: 12.5px;

  &--floor { max-width: 240px; }
}

.lvl-fw-key { margin-left: auto; display: flex; align-items: center; gap: 12px; opacity: .82; }
.lvl-fw-key-lbl {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  color: var(--cc-text-3, #97a0af);
  padding-right: 2px;
  border-right: 1px solid var(--cc-border, #e5e7eb);
  margin-right: 6px;
}
.lvl-fw-key-i {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);

  .sw {
    width: 13px;
    height: 13px;
    border-radius: 3px;
    border: 1px solid var(--cc-border, #e5e7eb);
    display: inline-grid;
    place-items: center;
  }
  .sw.is-anch {
    background: color-mix(in srgb, var(--cc-accent, #4f46e5) 85%, #000);
    border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 85%, #000);
    color: #fff;
  }
  .sw.is-sugg {
    border: 1px dashed color-mix(in srgb, var(--cc-accent, #4f46e5) 55%, transparent);
    background: transparent;
  }
  .sw.is-na {
    background: repeating-linear-gradient(135deg,
      var(--cc-canvas, #fafbfc) 0 3px, var(--cc-border-soft, #eef0f3) 3px 4px);
  }
  .lvl-fw-cov, .sw { transform: scale(.92); }
}

/* -------------------------------------------------------------------- explain */

.lvl-fw-explain {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-surface, #ffffff);
}
.lvl-fw-explain-ic {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--cc-canvas, #fafbfc);
  color: var(--cc-text-3, #97a0af);

  ::v-deep .v-icon { color: inherit; }
}
.lvl-fw-explain-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  .t { font-size: 13px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .s { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.5; }
}

/* ----------------------------------------------------------------------- grid */

.lvl-fw-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 14px;
  align-items: start;
}
.lvl-fw-gridwrap { min-width: 0; display: flex; flex-direction: column; gap: 12px; }

.lvl-fw-grid {
  display: grid;
  gap: 3px;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 12px;
  padding: 10px;
  /* The ladder can be wider than the pane, so it scrolls sideways. overflow-y MUST be
     stated: CSS forces the other axis to `auto` as soon as one axis is not `visible`
     (writing `overflow-y: visible` here is overridden too — only an explicit
     non-visible value works). Left alone, this grid becomes a VERTICAL scroll
     container with nothing to scroll, and the wheel targets it instead of the view
     behind it — the scrollbar drags but the wheel does nothing. It never needs to
     scroll vertically: it sizes to its own content and the view owns the scroll. */
  overflow-x: auto;
  overflow-y: hidden;

  /* Each slot wrapper (see the template) is there only so Vue moves a cell as one
     unit; it takes no part in layout, so its cell is the grid item. Vuetify also
     leaves an empty <span class="v-tooltip"> beside the activator it lifts out,
     which would otherwise take a grid slot of its own and push every later cell
     one column on. (The tooltip's content is detached to [data-app], so it is
     never a grid item either.) */
  > .lvl-fw-slot,
  > .lvl-fw-slot > .v-tooltip {
    display: contents;
  }
}

.lvl-fw-corner {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .03em;
  color: var(--cc-text-3, #97a0af);
  display: flex;
  align-items: flex-end;
  padding: 0 0 6px 4px;
}

.lvl-fw-colh {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding: 6px 8px;
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-text-2, #5b6472);
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);
  text-align: center;

  .tk {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: grid;
    place-items: center;
    flex: none;
  }
}

.lvl-fw-rowh {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  font-size: 12px;
  color: var(--cc-text-2, #5b6472);
  min-height: 34px;

  .g {
    display: inline-grid;
    place-items: center;
    min-width: 22px;
    height: 20px;
    padding: 0 5px;
    border-radius: 5px;
    background: var(--cc-canvas, #fafbfc);
    font-size: 11px;
    font-weight: 700;
    color: var(--cc-text-2, #5b6472);
  }
  .cl {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 10px;
    font-weight: 700;
    color: var(--cc-text-3, #97a0af);
  }
  &.is-ceil {
    border-top: 2px solid var(--cc-accent, #4f46e5);
    .cl { color: var(--cc-accent, #4f46e5); }
  }
  &.is-above { opacity: .6; }
}

.lvl-fw-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-height: 34px;
  padding: 4px 8px;
  border: 1px solid var(--cc-border-soft, #eef0f3);
  border-radius: 7px;
  background: var(--cc-surface, #ffffff);
  font: inherit;
  cursor: pointer;
  text-align: left;

  &:hover { border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent); }
  &.is-empty { cursor: default; }
  &.is-empty:hover { border-color: var(--cc-border-soft, #eef0f3); }
  &.is-na {
    border: none;
    background: repeating-linear-gradient(135deg,
      transparent 0 5px, var(--cc-border-soft, #eef0f3) 5px 6px);
    opacity: .5;
    cursor: default;
  }
  &.is-sugg { border: 1px dashed color-mix(in srgb, var(--cc-accent, #4f46e5) 55%, transparent); }
  &.is-anch {
    background: color-mix(in srgb, var(--cc-accent, #4f46e5) 85%, #000) !important;
    border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 85%, #000);
    .lvl-fw-code { color: #fff; }
  }
  &.is-sel { box-shadow: 0 0 0 2px var(--cc-accent, #4f46e5) inset; }
  &.is-above { opacity: .45; }
  &:focus-visible { outline: 2px solid var(--cc-accent, #4f46e5); outline-offset: 1px; }
}

.lvl-fw-code {
  font-size: 10.5px;
  font-weight: 650;
  color: var(--cc-text-3, #97a0af);
  letter-spacing: .01em;
}
.lvl-fw-n {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--cc-text, #1f242d);
  font-variant-numeric: tabular-nums;
}
.lvl-fw-mark {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #fff;

  b { font-size: 9.5px; letter-spacing: .03em; }
  .lvl-fw-n { color: #fff; margin-left: 2px; }
  &.is-pending { opacity: .45; }
}
.lvl-fw-sugg { font-size: 9.5px; font-weight: 700; color: var(--cc-accent, #4f46e5); }

.lvl-fw-cov {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid var(--cc-border, #e5e7eb);
  color: var(--cc-text-3, #97a0af);
  background: var(--cc-surface, #ffffff);

  &.is-met {
    color: var(--cc-ok-fg, #0e9f5b);
    border-color: color-mix(in srgb, var(--cc-ok-fg, #0e9f5b) 45%, transparent);
    background: color-mix(in srgb, var(--cc-ok-fg, #0e9f5b) 8%, var(--cc-surface, #fff));
  }
  &.is-thin {
    color: var(--cc-warn-fg, #b25e09);
    border-style: dashed;
    border-color: color-mix(in srgb, var(--cc-warn-fg, #b25e09) 55%, transparent);
  }
  &.is-gap {
    color: var(--cc-warn-fg, #b25e09);
    border-color: color-mix(in srgb, var(--cc-warn-fg, #b25e09) 45%, transparent);
    background: color-mix(in srgb, var(--cc-warn-fg, #b25e09) 12%, var(--cc-surface, #fff));
  }
}

/* ---------------------------------------------------------------------- panel */

.lvl-fw-panel {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 12px;
  background: var(--cc-surface, #ffffff);
  position: sticky;
  top: 8px;
  overflow: hidden;
}
.lvl-fw-panel-h {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);

  > div { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
  .c { font-size: 13.5px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .s { font-size: 11.5px; color: var(--cc-text-3, #97a0af); }
}
.lvl-fw-close {
  flex: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--cc-text-3, #97a0af);
  padding: 0;
}

.lvl-fw-panel-empty {
  padding: 18px 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--cc-text-3, #97a0af);

  p { margin: 0; font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.5; }
  p.s { font-size: 12px; color: var(--cc-text-3, #97a0af); }
}

.lvl-fw-list { max-height: 420px; overflow-y: auto; }

.lvl-fw-role {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);

  &:last-child { border-bottom: none; }
  &.is-anch { box-shadow: inset 3px 0 0 var(--cc-accent, #4f46e5); }
}
.lvl-fw-role-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  .t { font-size: 12.5px; font-weight: 650; color: var(--cc-text, #1f242d); }
  .s { font-size: 11px; color: var(--cc-text-3, #97a0af); }
}

.lvl-fw-set {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  border: 1px solid color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent);
  background: var(--cc-surface, #ffffff);
  border-radius: 8px;
  padding: 4px 9px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-accent, #4f46e5);
  cursor: pointer;

  &:hover { background: color-mix(in srgb, var(--cc-accent, #4f46e5) 8%, transparent); }
}

.lvl-fw-route {
  flex: none;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: transparent;
  border-radius: 8px;
  padding: 4px 9px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-text-2, #5b6472);
  cursor: pointer;

  &:hover { border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent); }
}

/* The panel drops under the grid before the grid itself has to shrink. */
@media (max-width: 1100px) {
  .lvl-fw-body { grid-template-columns: minmax(0, 1fr); }
  .lvl-fw-panel { position: static; }
}
</style>
